import { useState } from "react";
import {
  useGetPaymentMethodQuery,
  useDeletePaymentMethodMutation,
} from "@/redux/feature/paymentMethod/paymentMethod";
import Modal from "@/components/ui/Modal";
import { AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { useAppSelector } from "@/redux/hooks/hooks";
import { useGetBalanceQuery } from "@/redux/feature/creatorBalance/creatorBalanceApi";
import PaymentMethodModal from "./paymentMethodModal/PaymentMethodModal";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { useGetMeQuery } from "@/redux/feature/user/userApi";
import PaymentMethodsSection from "./PaymentMethodsSection";
import WalletBalanceSection from "./WalletBalanceSection";

export interface PaymentMethodDetails {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  branchName: string;
  routingCode: string;
  country: string;
  bankAddress: string;
  contactNumber: string;
}

export interface PaymentMethod {
  _id: string;
  type: "bank";
  label: string;
  isDefault: boolean;
  details: PaymentMethodDetails;
}

interface PaymentMethodsResponse {
  data: PaymentMethod[];
}

const Earnings = () => {
  // User data
  const { user } = useAppSelector((state) => state.auth);
  const { data: userData } = useGetMeQuery(user?.email);
  const creatorId = userData?.data?.creator_id as string;

  // Balance data
  const { data: balanceData } = useGetBalanceQuery(
    { email: user?.email },
    { skip: !user?.email }
  );
  const balance = balanceData?.data?.balance ?? 0;
  const pendingBalance = balanceData?.data?.pendingBalance ?? 0;

  // Payment methods data
  const {
    data: paymentMethodsData,
    isLoading,
    isError,
  } = useGetPaymentMethodQuery(creatorId, { skip: !creatorId });

  const paymentMethods =
    (paymentMethodsData as PaymentMethodsResponse)?.data ?? [];

  // Payment method mutations
  const [deletePaymentMethod, { isLoading: isDeleting }] =
    useDeletePaymentMethodMutation();

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null
  );

  /**
   * Handles opening the add bank account modal
   */
  const handleAddBankAccount = () => {
    setIsAddModalOpen(true);
  };

  /**
   * Handles opening the edit account modal
   * @param method - The payment method to edit
   */
  const handleEditAccount = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setIsEditModalOpen(true);
  };

  /**
   * Handles deleting a payment method
   * @param id - The ID of the payment method to delete
   */
  const handleDeleteAccount = async (id: string) => {
    await handleApiRequest({
      payload: id,
      requestFunction: deletePaymentMethod,
      onSuccess: () => {},
      onError: (error) => {
        toast.error("Failed to delete payment method");
        console.error("Delete error:", error);
      },
    });
  };

  /**
   * Closes all modals and resets the selected method
   */
  const closeModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedMethod(null);
  };

  /**
   * Masks the account number for display
   * @param accountNumber - The account number to mask
   * @returns The masked account number
   */
  const maskAccountNumber = (accountNumber: string): string => {
    if (!accountNumber) return "••••";
    if (accountNumber.length <= 4) return "••••";
    return `•••• ${accountNumber.slice(-4)}`;
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <div className="animate-pulse">Loading payment methods...</div>
      </div>
    );
  }

  // Render error state
  if (isError) {
    return (
      <div className="flex items-center justify-center py-10 bg-red-50 text-red-600 rounded-lg">
        <AlertCircle size={18} className="mr-2" />
        <span>Error loading payment methods. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h5 className="text-xl font-semibold">Earnings</h5>

      <div className="p-4 mt-5 border border-[#DEE1E7] rounded-xl">
        {/* Wallet Balance Section */}
        <WalletBalanceSection
          balance={balance}
          pendingBalance={pendingBalance}
        />

        {/* Payment Methods Section */}
        <PaymentMethodsSection
          paymentMethods={paymentMethods}
          onAddBankAccount={handleAddBankAccount}
          onEditAccount={handleEditAccount}
          onDeleteAccount={handleDeleteAccount}
          isDeleting={isDeleting}
          maskAccountNumber={maskAccountNumber}
        />
      </div>

      {/* Add Payment Method Modal */}
      <Modal isOpen={isAddModalOpen} setOpen={setIsAddModalOpen} width="500px">
        <PaymentMethodModal onClose={closeModals} creatorId={creatorId ?? ""} />
      </Modal>

      {/* Edit Payment Method Modal */}
      {selectedMethod && (
        <Modal
          isOpen={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          width="500px"
        >
          <PaymentMethodModal
            onClose={closeModals}
            creatorId={creatorId ?? ""}
            isEdit={true}
            paymentMethodId={selectedMethod._id}
            initialData={{
              type: selectedMethod.type,
              label: selectedMethod.label,
              isDefault: selectedMethod.isDefault,
              details: selectedMethod.details,
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default Earnings;
